import React from "react";
import { translateService } from "../../services/imports";

// How a cloud signing tool's key is certified, the certificate's subject and
// its organizational unit (the gateway's signer-certification-options, its
// organization rules of 2026-10-18). The options say, per kind and
// certification, whether the workspace may take it (unavailable ones are
// shown disabled with their reason, never hidden), and whether the subject is
// the organization's vetted identity (subjectFixed: read-only, an identifier
// picked) or typed by the owner. Used by the add dialog (its certification
// and subject steps, the unit beside the name) and by "Request certificate".

const t = (k, ...a) => translateService.t(`signers.add.${k}`, ...a);
const ts = (k, ...a) => translateService.t(`signers.${k}`, ...a);
const IDENTIFIER = /^[A-Z]{3}[A-Z]{2}-\S+$/;
// the kind's suffix in the common name ("<organization> SEAL"); its length
// is what the 64-character common name leaves for the organization's name
const SUFFIX = { CLOUD_REMOTE_SEAL_SIG: "SEAL", CLOUD_REMOTE_CA: "CA" };
export const nameLimit = (kind) => 64 - 1 - (SUFFIX[kind && kind.kind] || "CA").length;
export const commonNameOf = (kind, organizationName) =>
  organizationName ? `${organizationName} ${SUFFIX[kind && kind.kind] || "CA"}` : "";

// ---- the certificate's organizational unit -------------------------------------------

// Georgian letters by the national transliteration system
const GEORGIAN = {
  ა: "a", ბ: "b", გ: "g", დ: "d", ე: "e", ვ: "v", ზ: "z", თ: "t", ი: "i", კ: "k", ლ: "l", მ: "m", ნ: "n",
  ო: "o", პ: "p", ჟ: "zh", რ: "r", ს: "s", ტ: "t", უ: "u", ფ: "p", ქ: "k", ღ: "gh", ყ: "q", შ: "sh", ჩ: "ch",
  ც: "ts", ძ: "dz", წ: "ts", ჭ: "ch", ხ: "kh", ჯ: "j", ჰ: "h",
};
const UNIT_CHAR = /^[A-Za-z0-9'()+,\-./:?]$/;
const UNIT_RULE = /^[A-Za-z0-9'()+,\-./:?]+( [A-Za-z0-9'()+,\-./:?]+)*$/;
export const UNIT_MAX = 64;

// The clean name the certificate carries (X.520 PrintableString), from the
// display name: Georgian transliterated, English letters, digits and
// ' ( ) + , - . / : ? kept, anything else a space; spaces collapsed and
// trimmed; at most 64, cut at a word boundary.
export const unitFrom = (name) => {
  let s = Array.from(String(name || ""))
    .map((c) => {
      const g = GEORGIAN[c] || GEORGIAN[c.toLowerCase()];
      if (g) return g;
      return UNIT_CHAR.test(c) ? c : " ";
    })
    .join("")
    .replace(/\s+/g, " ")
    .trim();
  if (s.length > UNIT_MAX) {
    const cut = s.slice(0, UNIT_MAX + 1);
    const at = cut.lastIndexOf(" ");
    s = (at > 0 ? cut.slice(0, at) : s.slice(0, UNIT_MAX)).trim();
  }
  return s;
};

export const unitValid = (unit) => !!unit && unit.length <= UNIT_MAX && UNIT_RULE.test(unit);

// ---- the options --------------------------------------------------------------------

export const emptyCertificate = (kind, unit = "") => ({
  certification: "",
  organizationName: "",
  organizationIdentifier: "",
  country: "",
  validityYears: kind && kind.defaultValidityYears ? String(kind.defaultValidityYears) : "",
  organizationalUnit: unitFrom(unit),
  unitEdited: false,
});

// the gateway's options for this kind, in its order
export const optionsOf = (kind, options) => ((options && options.options) || []).filter((o) => kind && o.kind === kind.kind);

const optionOf = (kind, options, certification) => optionsOf(kind, options).find((o) => o.certification === certification);

const ownerUploads = (value, catalogue) =>
  !!((catalogue && catalogue.certifications) || []).find((c) => c.certification === value.certification && c.ownerUploadsCertificate);

const identifiersOf = (options) => (options && options.organization && options.organization.identifiers) || [];

const fixed = (options) => !!(options && options.subjectFixed);

// the identifier the certificate names: the one picked, or the only one -
// always sent, the gateway requires it for a vetted organization even then
const pickedIdentifier = (value, options) => {
  const ids = identifiersOf(options);
  if (ids.length === 1) return ids[0].organizationIdentifier;
  return value.organizationIdentifier;
};

// errors by field, for the given part ("certification" | "subject" | "unit")
export const checkCertificate = (kind, value, catalogue, options, part) => {
  const errors = {};
  if (part === "certification") {
    const option = optionOf(kind, options, value.certification);
    if (!option || !option.available) errors.certification = t("errors.certification");
  }
  if (part === "subject") {
    if (fixed(options)) {
      if (identifiersOf(options).length > 1 && !value.organizationIdentifier) errors.organizationIdentifier = ts("identifier.pick");
    } else {
      const required = (kind && kind.subjectRequired) || [];
      const name = value.organizationName.trim();
      if ((required.includes("organizationName") && !name) || name.length > nameLimit(kind))
        errors.organizationName = t("errors.organizationName", nameLimit(kind));
      const id = value.organizationIdentifier.trim();
      if ((required.includes("organizationIdentifier") && !id) || (id && (!IDENTIFIER.test(id) || id.length > 64)))
        errors.organizationIdentifier = t("errors.organizationIdentifier");
      const country = value.country.trim();
      if (country && !/^[A-Z]{2}$/.test(country)) errors.country = t("errors.country");
    }
    if (!ownerUploads(value, catalogue)) {
      const years = Number(value.validityYears);
      if (value.validityYears !== "" && !(Number.isInteger(years) && years >= 1 && years <= 10))
        errors.validityYears = t("errors.validityYears");
    }
  }
  if (part === "unit" && !unitValid(value.organizationalUnit)) errors.organizationalUnit = ts("unit.invalid");
  return errors;
};

// the request's members: certification, subject (a vetted organization's:
// only the identifier and the unit), validityYears (none for a certification
// whose PKI decides it)
export const certificateBody = (value, catalogue, options) => {
  let subject;
  if (fixed(options)) {
    subject = { organizationalUnit: value.organizationalUnit };
    const id = pickedIdentifier(value, options);
    if (id) subject.organizationIdentifier = id;
  } else {
    subject = { organizationName: value.organizationName.trim(), organizationalUnit: value.organizationalUnit };
    if (value.organizationIdentifier.trim()) subject.organizationIdentifier = value.organizationIdentifier.trim();
    if (value.country.trim()) subject.country = value.country.trim();
  }
  const body = { certification: value.certification, subject };
  if (!ownerUploads(value, catalogue) && value.validityYears !== "") body.validityYears = Number(value.validityYears);
  return body;
};

// a refusal that names one of the subject's members (in its detail: the
// gateway names the parameter there): the field to show it under, or null
export const subjectFieldOf = (problemBody) => {
  const text = JSON.stringify(problemBody || {});
  if (text.includes("organizationalUnit")) return "organizationalUnit";
  if (text.includes("organizationIdentifier")) return "organizationIdentifier";
  if (text.includes("organizationName")) return "organizationName";
  if (text.includes("subject.country")) return "country";
  return null;
};

// A vetted organization's subject refused: its records at validate.ge changed
// while the dialog was open (an identifier no longer listed, a legal name now
// too long). Rare: said plainly, close and start again - no re-reading.
export const vettedSubjectRefused = (code, problemBody, options) =>
  fixed(options) &&
  (code === "ORGANIZATION_NAME_TOO_LONG" ||
    ((code === "SUBJECT_REQUIRED" || code === "PARAMETER_IS_INVALID") &&
      ["organizationIdentifier", "organizationName", "country"].includes(subjectFieldOf(problemBody))));

const field = (id, label, input, error, hint) => (
  <label className="signer-dialog__field" htmlFor={id}>
    <span>{label}</span>
    {input}
    {hint && <small>{hint}</small>}
    {error && <em className="signer-dialog__error">{error}</em>}
  </label>
);

// "Name in the certificate", below the display name: prefilled from it until
// the owner edits it, checked while typing
export function UnitField({ value, error, onChange }) {
  const shown = error || (value.organizationalUnit && !unitValid(value.organizationalUnit) ? ts("unit.invalid") : null);
  return field(
    "signer-unit",
    ts("unit.label"),
    <input
      id="signer-unit"
      type="text"
      maxLength={UNIT_MAX}
      spellCheck="false"
      value={value.organizationalUnit}
      onChange={(e) => onChange({ organizationalUnit: e.target.value, unitEdited: true })}
    />,
    shown,
    ts("unit.help")
  );
}

// props: kind, catalogue, options (signer-certification-options), value,
// errors, onChange(patch), part ("certification" | "subject" | "both")
export default function CertificateFields({ kind, catalogue, options, value, errors = {}, onChange, part = "both" }) {
  const required = (kind && kind.subjectRequired) || [];
  const uploads = ownerUploads(value, catalogue);
  const org = (options && options.organization) || {};
  const ids = identifiersOf(options);
  const option = optionOf(kind, options, value.certification);
  const idLabel = (id) => `${translateService.t(`signers.identifierType.${id.type}`) || id.type}: ${id.value}`;
  const validity =
    !uploads &&
    field(
      "signer-validity",
      t("validityYears"),
      <input
        id="signer-validity"
        type="number"
        min={1}
        max={10}
        value={value.validityYears}
        onChange={(e) => onChange({ validityYears: e.target.value })}
      />,
      errors.validityYears
    );
  return (
    <React.Fragment>
      {part !== "subject" && (
        <fieldset className="signer-dialog__group">
          <legend>{t("certification")}</legend>
          {optionsOf(kind, options).map((o) => {
            const entry = ((catalogue && catalogue.certifications) || []).find((c) => c.certification === o.certification) || {};
            const trust = translateService.t(`signers.add.trust.${o.certification}`);
            return (
              <label key={o.certification} className={`signer-dialog__choice ${o.available ? "" : "is-off"}`}>
                <input
                  type="radio"
                  name="certification"
                  disabled={!o.available}
                  checked={value.certification === o.certification}
                  onChange={() => o.available && onChange({ certification: o.certification })}
                />
                <span>
                  <strong>{o.certificationLabel}</strong>
                  {!o.available && o.unavailableReasonLabel && <small>{o.unavailableReasonLabel}</small>}
                  {o.available && trust && <small>{trust}</small>}
                  {o.available && entry.ownerUploadsCertificate && <small>{t("ownerUploads")}</small>}
                </span>
              </label>
            );
          })}
          {errors.certification && <em className="signer-dialog__error">{errors.certification}</em>}
        </fieldset>
      )}
      {part !== "certification" && fixed(options) && (
        <fieldset className="signer-dialog__group">
          <legend>{t("subject")}</legend>
          <dl className="signer-dialog__facts">
            <dt>{ts("subject.legalName")}</dt>
            <dd>{org.legalName}</dd>
            <dt>{ts("subject.country")}</dt>
            <dd>{org.country}</dd>
            {option && option.commonName && (
              <React.Fragment>
                <dt>{ts("subject.commonName")}</dt>
                <dd>{option.commonName}</dd>
              </React.Fragment>
            )}
            {ids.length === 1 && (
              <React.Fragment>
                <dt>{ts("identifier.label")}</dt>
                <dd>{idLabel(ids[0])}</dd>
              </React.Fragment>
            )}
          </dl>
          {ids.length > 1 && (
            <div className="signer-dialog__group" role="radiogroup" aria-label={ts("identifier.pick")}>
              <p className="signer-dialog__label">{ts("identifier.pick")}</p>
              {ids.map((id) => (
                <label key={id.organizationIdentifier} className="signer-dialog__choice">
                  <input
                    type="radio"
                    name="organization-identifier"
                    checked={value.organizationIdentifier === id.organizationIdentifier}
                    onChange={() => onChange({ organizationIdentifier: id.organizationIdentifier })}
                  />
                  <span>{idLabel(id)}</span>
                </label>
              ))}
              {errors.organizationIdentifier && <em className="signer-dialog__error">{errors.organizationIdentifier}</em>}
            </div>
          )}
          <p className="signer-dialog__hint">{ts("subject.fixedHelp")}</p>
          {validity}
        </fieldset>
      )}
      {part !== "certification" && !fixed(options) && (
        <fieldset className="signer-dialog__group">
          <legend>{t("subject")}</legend>
          {field(
            "signer-organization-name",
            t(required.includes("organizationName") ? "organizationName" : "organizationNameOptional"),
            <input
              id="signer-organization-name"
              type="text"
              maxLength={nameLimit(kind)}
              value={value.organizationName}
              onChange={(e) => onChange({ organizationName: e.target.value })}
            />,
            errors.organizationName,
            value.organizationName.trim() ? `${ts("subject.commonName")}: ${commonNameOf(kind, value.organizationName.trim())}` : null
          )}
          {field(
            "signer-organization-identifier",
            t(required.includes("organizationIdentifier") ? "organizationIdentifier" : "organizationIdentifierOptional"),
            <input
              id="signer-organization-identifier"
              type="text"
              maxLength={64}
              placeholder="NTRGE-…"
              value={value.organizationIdentifier}
              onChange={(e) => onChange({ organizationIdentifier: e.target.value })}
            />,
            errors.organizationIdentifier
          )}
          {field(
            "signer-country",
            t("country"),
            <input
              id="signer-country"
              type="text"
              maxLength={2}
              placeholder="GE"
              value={value.country}
              onChange={(e) => onChange({ country: e.target.value.toUpperCase() })}
            />,
            errors.country
          )}
          {validity}
        </fieldset>
      )}
    </React.Fragment>
  );
}
